/**
 * Pure helpers for the Objects page: merging the annotation summary into the
 * nodes, and the "can train a child" gate with its wording. The tree itself —
 * building, searching, walking — lives in `@utils/taxonomyTree`, shared with
 * every other taxonomy tree.
 */

import { PROJECT_MODE } from '@utils/constants';

/** Shown wherever counts would be, until the annotation summary has loaded. */
const COUNTS_UNAVAILABLE = 'Annotation counts are not available yet';

/** Shown wherever counts would be, when the annotation summary request failed. */
const COUNTS_FAILED = 'Couldn’t load annotation counts';

/** Why `node` has no counts to show: the summary failed, or has not loaded yet. */
export const countsUnavailableReason = node =>
	node?.annotation_summary_failed ? COUNTS_FAILED : COUNTS_UNAVAILABLE;

/** The annotation threshold from the summary, or `null` before it arrives (there is no fallback). */
export const minAnnotationsOf = node =>
	typeof node?.min_annotations === 'number' ? node.min_annotations : null;

/** The tile threshold from the summary, or `null` before it arrives (there is no fallback). */
const minTilesOf = node => (typeof node?.min_tiles === 'number' ? node.min_tiles : null);

/**
 * What the summary's "tiles" are called for a project type: an orthophoto is cut
 * into tiles, while a single-image project's unit is the image itself.
 */
export const sampleUnitOf = mode => (mode === PROJECT_MODE.SINGLE_IMAGE ? 'image' : 'tile');

/** A count from the summary as a non-negative number; anything else is 0. */
const countOf = value => Math.max(0, Number(value) || 0);

const requirement = (count, required) => ({
	count,
	required,
	missing: Math.max(0, required - count),
	met: count >= required,
	percent: required > 0 ? Math.min(100, Math.round((count / required) * 100)) : 100,
});

/**
 * Each count the server checks before a child can be trained from `node`, against
 * its threshold; `null` until the summary brings the thresholds (`tiles` alone is
 * `null` without a tile threshold). `countsMet` does not mean trainable: the server
 * also wants new annotations since the last training, which only `can_be_trained` tells.
 *
 * @returns {{ annotations: object, tiles: object | null, countsMet: boolean } | null}
 *   each requirement as `{ count, required, missing, met, percent }`
 */
export const trainingRequirements = node => {
	const annotationsRequired = minAnnotationsOf(node);
	if (annotationsRequired === null) return null;
	const annotations = requirement(countOf(node?.annotations), annotationsRequired);
	const tilesRequired = minTilesOf(node);
	const tiles =
		tilesRequired === null ? null : requirement(countOf(node?.annotation_tiles), tilesRequired);
	return { annotations, tiles, countsMet: annotations.met && (tiles?.met ?? true) };
};

/** `word`, pluralized for `count`: "1 tile", "5 images". */
export const pluralize = (count, word) => `${word}${count === 1 ? '' : 's'}`;

/** "1 more tile", "5 more images": the tooltip and the details card both word shortfalls with it. */
export const moreOf = (count, word) => `${count.toLocaleString()} more ${pluralize(count, word)}`;

const NEEDS_NEW_ANNOTATIONS = 'Needs new annotations since the last training';

// "110 more annotations and 6 more tiles": each count still short of its threshold.
const shortfallOf = ({ annotations, tiles }, mode) =>
	[
		annotations.missing > 0 && moreOf(annotations.missing, 'annotation'),
		tiles?.missing > 0 && moreOf(tiles.missing, sampleUnitOf(mode)),
	]
		.filter(Boolean)
		.join(' and ');

// With every count met, the only other server condition is new annotations since the
// last training; otherwise what is short, to train `target`.
const describeShortfall = (requirements, mode, target) =>
	requirements.countsMet
		? NEEDS_NEW_ANNOTATIONS
		: `Needs ${shortfallOf(requirements, mode)} to train ${target}`;

/**
 * Why a child cannot be trained from `node` yet, in one line for the tree card's
 * tooltip; `null` when it can. Tiles are named for the project type (`mode`).
 * With both counts met, the remaining reason is inferred: the server's only
 * other condition is new annotations since the last training.
 */
export const describeMissing = (node, mode) => {
	if (canTrainChild(node)) return null;
	const requirements = trainingRequirements(node);
	if (!requirements) return countsUnavailableReason(node);
	return describeShortfall(requirements, mode, 'a child object');
};

/**
 * Why a child cannot be trained from one of `node`'s labels, worded like
 * `describeMissing`: the label's own counts (`annotations`, `tiles`) against the
 * node's thresholds. Only asked about a label the server has marked untrainable,
 * so met counts mean the same inferred reason, nothing new since the last training.
 */
export const describeLabelMissing = (node, { annotations, tiles }, mode) => {
	const requirements = trainingRequirements({ ...node, annotations, annotation_tiles: tiles });
	if (!requirements) return 'This label can’t be trained from yet';
	return describeShortfall(requirements, mode, 'from this label');
};

/**
 * The Label choices from a node's `annotation_labels` (see `mergeAnnotationSummary`):
 * the free-text labels annotators have used on the node, how many examples (and tiles)
 * each has, and which ones cannot be trained from — those the server flags, and any
 * label with no examples. The unlabelled examples (`label` null) back the "Default"
 * option, keyed by the empty string.
 *
 * @returns {{ labelOptions: string[], labelCounts: Object<string, number>,
 *   labelTiles: Object<string, number>, untrainable: Set<string> }}
 */
export const labelsFromSummary = (annotationLabels = []) => {
	const labelOptions = new Set();
	// No prototype: the keys are free text, and a label like "constructor" must not read Object's.
	const labelCounts = Object.create(null);
	const labelTiles = Object.create(null);
	const untrainable = new Set();
	annotationLabels.forEach(
		({
			label,
			total_number_of_annotations: count,
			total_number_of_tiles: tiles,
			can_be_trained,
		}) => {
			const key = label ?? '';
			if (key) labelOptions.add(key);
			labelCounts[key] = (labelCounts[key] ?? 0) + countOf(count);
			labelTiles[key] = (labelTiles[key] ?? 0) + countOf(tiles);
			if (can_be_trained === false) untrainable.add(key);
		}
	);
	// Once split by label, a label with no examples (Default too, when nothing is
	// unlabelled) cannot be trained from, whatever the server flagged.
	if (annotationLabels.length)
		['', ...labelOptions].forEach(key => {
			if (!(labelCounts[key] > 0)) untrainable.add(key);
		});
	return { labelOptions: [...labelOptions], labelCounts, labelTiles, untrainable };
};

/**
 * What a child of `node` trains from with `label` (`''` = Default): that label's
 * annotations and tiles, or the node's totals when the summary has no label split. A
 * label absent from the split, Default included, has none. Pass `labels` (from
 * `labelsFromSummary`) when it is already at hand, to skip parsing it again.
 *
 * @returns {{ annotations: number, tiles: number }}
 */
export const trainingSampleFor = (
	node,
	label = '',
	{ labelCounts, labelTiles } = labelsFromSummary(node?.annotation_labels)
) => {
	if (!Object.keys(labelCounts).length)
		return { annotations: countOf(node?.annotations), tiles: countOf(node?.annotation_tiles) };
	return { annotations: labelCounts[label] ?? 0, tiles: labelTiles[label] ?? 0 };
};

/**
 * Whether a child object can be trained from `node`: the summary's own answer
 * (enough tiles and annotations, and new ones since the last training). It ignores
 * `is_available`, though the summary's counts span every project type: the details card
 * checks it before offering Train, and the tree card before showing a training status.
 */
export const canTrainChild = node => node?.can_be_trained === true;

/**
 * Attach `GET /v2/taxonomy/annotations/summary` (as `getAnnotationSummary` returns
 * it) to the taxonomy nodes, adding only what the page reads. Every node gets the
 * shared thresholds (`min_annotations`, `min_tiles`); a listed node also gets
 * `annotations`, `annotation_tiles`, `can_be_trained` and `annotation_labels` (the
 * summary's `labels`; `label` null = unlabelled). With `failed` (the request failed,
 * nothing cached) every node is marked `annotation_summary_failed` instead.
 */
export const mergeAnnotationSummary = (nodes = [], summary = {}, { failed = false } = {}) => {
	if (failed) return nodes.map(node => ({ ...node, annotation_summary_failed: true }));
	const entries = summary?.nodes ?? [];
	const thresholds = {
		...(typeof summary?.required_number_of_annotations === 'number' && {
			min_annotations: summary.required_number_of_annotations,
		}),
		...(typeof summary?.required_number_of_tiles === 'number' && {
			min_tiles: summary.required_number_of_tiles,
		}),
	};
	const hasThresholds = Object.keys(thresholds).length > 0;
	if (!entries.length && !hasThresholds) return nodes;
	const byUuid = new Map(entries.map(entry => [entry.node_uuid, entry]));
	return nodes.map(node => {
		const entry = byUuid.get(node.uuid);
		const withThresholds = hasThresholds ? { ...node, ...thresholds } : node;
		if (!entry) return withThresholds;
		return {
			...withThresholds,
			annotations: entry.total_number_of_annotations ?? 0,
			annotation_tiles: entry.total_number_of_tiles ?? 0,
			can_be_trained: entry.can_be_trained === true,
			annotation_labels: entry.labels ?? [],
			annotation_projects: entry.projects ?? [],
		};
	});
};

const lockEntry = entry => ({
	...entry,
	can_be_trained: false,
	...(entry.labels && {
		labels: entry.labels.map(label => ({ ...label, can_be_trained: false })),
	}),
});

/**
 * `summary` (as `getAnnotationSummary` returns it) with the node `parentUuid`
 * no longer trainable — for the cache, right after a child is trained from it. Its labels
 * are locked with it, so the per-label view does not offer what the node refuses.
 */
export const lockParentInSummary = (summary, parentUuid) =>
	summary?.nodes
		? {
				...summary,
				nodes: summary.nodes.map(entry =>
					entry.node_uuid === parentUuid ? lockEntry(entry) : entry
				),
			}
		: summary;
